import {
  ID,
  LocalizedString,
  TaskPriorityOptions,
  TemplateSchedule,
  TimeFormatOptions,
  Timestamp,
} from '@griply/types';
import { getLocalizedStringValue, getScheduleLabel } from '@griply/utils';
import Typography from '@mui/material/Typography';
import { format, isSameDay } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Calendar, Repeat, Subtask } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { parseDateTime } from 'shared/utils/parse-date-time';

import * as Styled from './task-preview-card.style';

export type TaskPreviewCardProps = {
  name: LocalizedString;
  description: LocalizedString | null;
  priority: TaskPriorityOptions | null;
  deadline: Timestamp | null;
  reminderTime: number | null;
  schedule: TemplateSchedule | null;
  childIds: ID[] | null;

  today: Timestamp;
  timeFormat: TimeFormatOptions;
};

export const TaskPreviewCard: React.FC<TaskPreviewCardProps> = ({
  name,
  description,
  priority,
  deadline,
  reminderTime,
  schedule,
  childIds,

  today,
  timeFormat,
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const deadlineLabel = useMemo(() => {
    if (!deadline) {
      return undefined;
    }

    if (isSameDay(deadline, today)) {
      return t('general.labels.dates.today');
    }

    return format(deadline, 'd MMM');
  }, [deadline, t, today]);

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.CheckboxPlaceholder $priority={priority} />
        <Styled.HeaderContent>
          <Typography variant="body2">
            {getLocalizedStringValue(name, locale)}
          </Typography>
          {!!description && (
            <Styled.Description>
              {getLocalizedStringValue(description, locale)}
            </Styled.Description>
          )}
        </Styled.HeaderContent>
      </Styled.Header>

      <Styled.Body>
        <Styled.List>
          {!!childIds?.length && (
            <Styled.ListItem>
              <Styled.MetaTag>
                <Icon icon={Subtask} />
                <Typography variant="overline">{childIds.length}</Typography>
              </Styled.MetaTag>
            </Styled.ListItem>
          )}
          {!!deadlineLabel && (
            <Styled.ListItem>
              <Styled.MetaTag>
                <Icon icon={Calendar} />
                <Typography variant="overline">{deadlineLabel}</Typography>
              </Styled.MetaTag>
            </Styled.ListItem>
          )}
          {!!schedule && (
            <Styled.ListItem>
              <Styled.MetaTag>
                <Icon icon={Repeat} />
                <Typography variant="overline">
                  {getScheduleLabel(schedule, t)}
                </Typography>
              </Styled.MetaTag>
            </Styled.ListItem>
          )}
          {!!reminderTime && (
            <Styled.ListItem>
              <Styled.MetaTag>
                <Icon icon={Bell} />
                <Typography variant="overline">
                  {parseDateTime(reminderTime, timeFormat)}
                </Typography>
              </Styled.MetaTag>
            </Styled.ListItem>
          )}
        </Styled.List>
      </Styled.Body>
    </Styled.Container>
  );
};
